import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';

export interface User {
  id: string;
  email: string;
  role: 'user' | 'admin';
  kyc_status: string;
  is_platform_admin?: boolean;
  is_support_staff?: boolean;
  is_modal_operations?: boolean;
  auth_identity_id?: string;
  operations_grant_version?: number;
  company_id?: string;
  accessible_company_ids?: string[];
  support_write_permissions?: string[];
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string, confirmPassword: string) => Promise<void>;
  establishSession: (accessToken: string, successMessage?: string) => User;
  logout: () => void;
  operationsScope?: string;
  operationsCompanyId?: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeToken(token: string): User {
  const payload = JSON.parse(atob(token.split('.')[1]));
  const currentTime = Math.floor(Date.now() / 1000);
  if (payload.exp && payload.exp < currentTime) {
    throw new Error('Token expired');
  }
  return {
    id: payload.sub,
    email: payload.email,
    role: payload.role,
    company_id: typeof payload.company_id === 'string' ? payload.company_id : undefined,
    kyc_status: payload.kyc_status || 'pending',
    is_platform_admin: Boolean(payload.is_platform_admin),
    is_support_staff: Boolean(payload.is_support_staff),
    accessible_company_ids: Array.isArray(payload.accessible_company_ids) ? payload.accessible_company_ids : [],
    support_write_permissions: Array.isArray(payload.support_write_permissions) ? payload.support_write_permissions : [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token is still valid by making a test request
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        decodeToken(token);
        const response = await apiClient.get<User>('/auth/me');
        setUser(response.data);

        try {
          await apiClient.post('/auth/session-view', {
            source: 'session_restore',
          });
        } catch {
          // Session restore should not force logout if monitoring ping fails.
        }
      }
    } catch (error) {
      localStorage.removeItem('token');
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const establishSession = (accessToken: string, successMessage?: string): User => {
    const decodedUser = decodeToken(accessToken);

    localStorage.setItem('token', accessToken);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setUser(decodedUser);

    if (successMessage) {
      toast.success(successMessage);
    }

    return decodedUser;
  };

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { access_token } = response.data;
      return establishSession(access_token, 'Login successful!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      throw error;
    }
  };

  const register = async (email: string, password: string, confirmPassword: string) => {
    try {
      const response = await apiClient.post('/auth/register', {
        email,
        password,
        confirmPassword
      });
      const { access_token } = response.data;
      establishSession(access_token, 'Registration successful!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, establishSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
